import { apiRequest } from '@/shared/api/client';
import type { Envelope, MoneyValue, Paginated } from '@/shared/types/api';
import { compareDecimals, isDecimal } from '@/shared/utils/decimal';
import type {
  ApprovalInboxItem,
  DraftValidation,
  PurchaseRequest,
  ReorderResult,
} from '@/shared/types/domain';
import type { ListParams } from '@/features/organization/api';

// ── Requests and the unified cart ────────────────────────────────────────────

/**
 * REQ-05 — the controlled filter set for the request collection.
 *
 * A CLOSED shape, not a bag of query parameters. Every field maps to a filter
 * the server implements and refuses to widen: an unknown status, a purchasing
 * node outside the caller's scope, or a requester filter an actor may not use
 * comes back as a stated error rather than an empty page.
 *
 * `mine` is a SERVER scope, not a view over rows already fetched — see
 * `useRequestFilters`.
 *
 * A type alias rather than an interface, deliberately: an alias carries an
 * implicit index signature, so this assigns straight into `CrudPage`'s
 * `filterValues` without a cast that would let a typo through.
 */
export type RequestListFilters = {
  /** One or more `RequestStatus` values, comma separated. */
  status?: string;
  /** Purchasing node public id. `branch_id` remains the wire name. */
  branch_id?: string;
  /** Requester public id. Only accepted from an actor who sees more than their own. */
  requester_id?: string;
  /** Calendar days, `YYYY-MM-DD`. Compared as dates — never through a timezone. */
  required_by_from?: string;
  required_by_to?: string;
  /** Collection mode: true narrows to the caller's own requests, on the server. */
  mine?: boolean;
};

export const listRequests = (params: ListParams & RequestListFilters = {}) =>
  apiRequest<Paginated<PurchaseRequest>>('/requests', { query: { ...params } });

/** One pickable value. Never a profile — an identifier and a name. */
export interface RequestFilterOption {
  id: string;
  display_name: string;
}

/**
 * REQ-05 — what the filters may be set to, for the signed-in actor.
 *
 * ── One source, scoped like the list ────────────────────────────────────────
 * Assembling this from `/branches` and `/users` would need a permission four
 * request-visible seats do not hold, and would leave two scopes to keep in
 * agreement with what the filter accepts. The server derives each list from the
 * same clause the request list uses, so an option offered here is an option the
 * list will accept.
 *
 * `requesters` is built from requests the actor can already open, so it is not
 * a user directory and cannot be read as one.
 */
export interface RequestFilterOptions {
  statuses: { value: string; label: string }[];
  /** `is_archived` marks a closed node kept as a historical filter. */
  purchasing_nodes: (RequestFilterOption & { is_archived: boolean })[];
  /** Empty unless `can_see_others`. */
  requesters: RequestFilterOption[];
  /** Gates the requester filter AND the All / My Requests navigation. */
  can_see_others: boolean;
}

/**
 * R12-03 — a request's total as a figure a reader can rely on, or `null`.
 *
 * A draft's stored total is not its price: its lines carry no price until the
 * server validates them (the cart shows the validated figure), so a list
 * printing the stored "0.00 SAR" told a requester their basket was free. The
 * same holds for a need nobody has priced yet — an off-catalogue request with
 * procurement. A list says "—" for those; everything else keeps its exact
 * figure.
 */
export function knownTotal(
  request: Pick<PurchaseRequest, 'request_number' | 'totals'>,
): MoneyValue | null {
  const total = request.totals.total;

  if (!request.request_number) return null;
  if (isDecimal(total.amount) && compareDecimals(total.amount, '0') === 0) return null;

  return total;
}

export const getRequestFilterOptions = () =>
  apiRequest<Envelope<RequestFilterOptions>>('/requests/filter-options');

export const showRequest = (id: string) => apiRequest<Envelope<PurchaseRequest>>(`/requests/${id}`);

/**
 * CAT-07 — the caller's open cart, or null if they have none.
 *
 * A pure read. It used to create a cart, which made loading a page write a row
 * and gave a commercially suspended tenant a way to keep writing (COM-06).
 */
export const openCart = (branchId?: string, currency?: string) =>
  apiRequest<Envelope<PurchaseRequest | null>>('/cart', {
    query: { branch_id: branchId, currency },
  });

/**
 * Start a cart. The write half of what `GET /cart` used to do.
 *
 * FND-13 — an optional currency. Omitted, the organisation's own currency is
 * used and no commercial check runs. Supplied, this resolves to the caller's
 * existing open basket in that currency when there is one, so calling it again
 * is not a way to end up with two.
 */
export const startCart = (branchId?: string, currency?: string) =>
  apiRequest<Envelope<PurchaseRequest>>('/cart', {
    method: 'POST',
    body: { branch_id: branchId, currency },
  });

/**
 * Add or update a cart line.
 *
 * Only an item and a quantity are sent. Price, supplier and tax are resolved by
 * the server from the approved framework — the API refuses to accept them.
 */
export const setCartLine = (
  requestId: string,
  catalogueItemId: string,
  quantity: string,
  note?: string,
) =>
  apiRequest<Envelope<PurchaseRequest>>(`/requests/${requestId}/lines`, {
    method: 'PUT',
    body: { catalogue_item_id: catalogueItemId, quantity, note },
  });

/**
 * N6 — the quantity of an off-catalogue need its requester has not sent yet, on
 * its own line, by the line's id: the cart's route above finds a line by its
 * catalogue item, and a need has none. Only the quantity travels; the server
 * refuses a supplier, a price or an item here.
 */
export const setNeedQuantity = (requestId: string, lineId: string, quantity: string) =>
  apiRequest<Envelope<PurchaseRequest>>(`/requests/${requestId}/lines/${lineId}`, {
    method: 'PATCH',
    body: { quantity },
  });

export const removeCartLine = (requestId: string, lineId: string) =>
  apiRequest<Envelope<PurchaseRequest>>(`/requests/${requestId}/lines/${lineId}`, {
    method: 'DELETE',
  });

export const updateRequest = (requestId: string, body: Record<string, unknown>) =>
  apiRequest<Envelope<PurchaseRequest>>(`/requests/${requestId}`, { method: 'PATCH', body });

/** APR-01 — full preview. Writes nothing. */
export const validateRequest = (requestId: string) =>
  apiRequest<Envelope<DraftValidation>>(`/requests/${requestId}/validate`, { method: 'POST' });

/**
 * Submit.
 *
 * Carries an idempotency key so a double click or a retried request cannot
 * create two requests, two reservations or two approval workflows.
 */
/**
 * CAT-05 — seed a NEW draft from a prior request.
 *
 * Nothing commercial is carried across: the quantity is, and every line is
 * re-resolved against today's catalogue. The response says what carried over
 * and what could not, with a reason.
 */
export const reorderFromRequest = (requestId: string, idempotencyKey: string) =>
  apiRequest<Envelope<ReorderResult>>(`/requests/${requestId}/reorder`, {
    method: 'POST',
    idempotencyKey,
  });

/**
 * G02-01 — what a reorder hands to the cart it lands on: the lines that did
 * not carry over, each with the server's reason. Router state, never the URL:
 * it describes one navigation, not a place anybody could link to.
 */
export interface ReorderReport {
  reorderUnavailable: ReorderResult['unavailable'];
}

/** The report from a history entry — or nothing, when the entry carries none (or not this shape). */
export function reorderReportFrom(state: unknown): ReorderResult['unavailable'] {
  if (state === null || typeof state !== 'object') return [];

  const lines = (state as { reorderUnavailable?: unknown }).reorderUnavailable;

  if (!Array.isArray(lines)) return [];

  return lines.filter(
    (line): line is ReorderResult['unavailable'][number] =>
      line !== null &&
      typeof line === 'object' &&
      typeof (line as { name?: unknown }).name === 'string' &&
      typeof (line as { reason?: unknown }).reason === 'string',
  );
}

/*
 * C3 — every duplicate-sensitive command below takes its `idempotencyKey` from
 * the caller, who holds ONE per intended operation until it has an answer
 * (see `useOperationKeys`). Minting one in here made every click a new
 * operation, so a retry after a lost response could never be recognised.
 */
export const submitRequest = (requestId: string, idempotencyKey: string) =>
  apiRequest<Envelope<PurchaseRequest>>(`/requests/${requestId}/submit`, {
    method: 'POST',
    idempotencyKey,
  });

export const cancelRequest = (requestId: string, reason?: string) =>
  apiRequest<Envelope<PurchaseRequest>>(`/requests/${requestId}/cancel`, {
    method: 'POST',
    body: { reason },
  });

/**
 * N5 (29 Oct 2026) — back to sourcing from procurement processing, before any
 * order: the award is withdrawn and its draft agreement terminated. A reason is
 * always required.
 */
export const returnRequestToSourcing = (requestId: string, reason: string) =>
  apiRequest<Envelope<PurchaseRequest>>(`/requests/${requestId}/return-to-sourcing`, {
    method: 'POST',
    body: { reason },
  });

/**
 * N5-c-2 — the same sourced request, priced from its activated agreement: its
 * budget reserved once and the approval its policy asks for opened. Keyed, as a
 * submission is: a second press must not price it twice.
 */
export const priceFromAgreement = (requestId: string, idempotencyKey: string) =>
  apiRequest<Envelope<PurchaseRequest>>(`/requests/${requestId}/price-from-agreement`, {
    method: 'POST',
    idempotencyKey,
  });

/** REQ-02 — controlled intake. No supplier, no price. */
export const createOffCatalogueRequest = (body: Record<string, unknown>, idempotencyKey: string) =>
  apiRequest<Envelope<PurchaseRequest>>('/requests/off-catalogue', {
    method: 'POST',
    body,
    idempotencyKey,
  });

export const sendToSourcing = (requestId: string, notes?: string) =>
  apiRequest<Envelope<PurchaseRequest>>(`/requests/${requestId}/send-to-sourcing`, {
    method: 'POST',
    body: { notes },
  });

// ── Approvals (APR-05 / APR-06) ──────────────────────────────────────────────

export const listApprovals = (
  params: ListParams & { overdue?: boolean; delegated?: boolean } = {},
) => apiRequest<Paginated<ApprovalInboxItem>>('/approvals', { query: { ...params } });

export const showApproval = (workflowId: string) =>
  apiRequest<
    Envelope<{
      workflow: ApprovalInboxItem;
      request: PurchaseRequest | null;
      /** A-02 — the most each line may still be approved for, by line id (exact decimals). */
      ceilings?: Record<string, string>;
    }>
  >(`/approvals/${workflowId}`);

export interface DecisionInput {
  decision: 'approve' | 'reject' | 'return' | 'partial_approve';
  reason?: string;
  lines?: { line: string; approved_quantity: string; reason?: string }[];
}

export const decideApproval = (workflowId: string, input: DecisionInput, idempotencyKey: string) =>
  apiRequest<Envelope<{ workflow_status: string; request: PurchaseRequest | null }>>(
    `/approvals/${workflowId}/decide`,
    { method: 'POST', body: { ...input }, idempotencyKey },
  );
